// ─────────────────────────────────────────────────────────────────────────────
//  graphs/HorizontalBarChart.jsx
//
//  Self-contained:  transform  •  tooltips  •  legend  •  render
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import ReactApexChart from "react-apexcharts";
import {
    ChartAnimationWrapper, ErrorMessage,
    getBaseChartConfig, getSeriesColors, getSingleUnit,
    formatNumberWithSuffix, formatYAxisSmart, sanitiseSeriesName,
    formatSeries, detectUnitField,
    BIOMEDICAL_WASTE_COLORS, DEFAULT_COLORS,
    axisLabelStyle, monoLabelStyle,
} from "../mainUnit";

// ─── TRANSFORM ────────────────────────────────────────────────────────────────
const transformData = (rawData, groupedField, title, dataSourceType) => {
    if (!rawData || rawData.length === 0) return null;

    const xAxisField   = groupedField?.[0];
    const stackByField = groupedField?.[1] || null;
    let series = [], categories = [], colorMap = {}, unitsMap = {}, showCategoryLegend = true;

    if (stackByField && xAxisField) {
        const groupedData = {}, stackValues = new Set();
        rawData.forEach(item => {
            const xVal     = item[xAxisField];
            const stackVal = item[stackByField];
            const value    = parseFloat(item.sum_value || item.min_value) || 0;
            stackValues.add(stackVal);
            if (!groupedData[stackVal]) groupedData[stackVal] = {};
            groupedData[stackVal][xVal] = (groupedData[stackVal][xVal] || 0) + value;
        });

        const totals = {};
        rawData.forEach(item => {
            const xVal  = item[xAxisField];
            const value = parseFloat(item.sum_value || item.min_value) || 0;
            totals[xVal] = (totals[xVal] || 0) + value;
        });
        categories = Object.keys(totals);

        // ★ FIX: capture which field owns the unit so we can decide whether
        //   to set series[].unit (stackByField case) or leave it null (xAxisField case).
        //   When unit is on xAxisField, setting series[].unit via last-write-wins
        //   stackByUnitLookup produces the wrong unit for every series — the tooltip
        //   must instead read it from unitsMap[categoryLabel] at hover time.
        const { unitField: detectedUnitField, unitsMap: detectedUnitsMap } = detectUnitField(rawData, xAxisField, stackByField);
        unitsMap = detectedUnitsMap;
        const unitOnStackBy = detectedUnitField === stackByField;

        // Only build the stackBy lookup when it's actually the unit-bearing field
        const stackByUnitLookup = {};
        rawData.forEach(item => {
            if (!item.unit || !stackByField) return;
            stackByUnitLookup[String(item[stackByField] ?? '')] = item.unit;
        });

        [...stackValues].forEach((stackVal, index) => {
            const color = BIOMEDICAL_WASTE_COLORS[stackVal] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
            colorMap[stackVal] = color;
            // Only assign series-level unit when it genuinely belongs to the stack field.
            // When unit belongs to xAxisField, leave null so the tooltip resolves it
            // per hover via unitsMap[categoryLabel] — the only source that is per-bar correct.
            const unit = unitOnStackBy ? (stackByUnitLookup[stackVal] ?? null) : null;
            series.push({ name: String(stackVal), unit, data: categories.map(xVal => groupedData[stackVal][xVal] || 0), color });
        });
    } else if (xAxisField) {
        showCategoryLegend = true;
        const aggregated = {};
        rawData.forEach(item => {
            const label = item[xAxisField] || 'Unknown';
            aggregated[label] = (aggregated[label] || 0) + (parseFloat(item.sum_value || item.min_value) || 0);
            if (item.unit) unitsMap[label] = item.unit;
        });
        const labels = Object.keys(aggregated);
        labels.forEach((lbl, idx) => { colorMap[lbl] = BIOMEDICAL_WASTE_COLORS[lbl] || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]; });
        series = [{ data: labels.map(lbl => ({ x: lbl, y: aggregated[lbl], fillColor: colorMap[lbl] })) }];
    }

    return {
        graphType: 'bar', dataSourceType, title: title || 'Analysis',
        categories, series: formatSeries(series),
        colors: DEFAULT_COLORS, colorMap, unitsMap,
        stacked: !!stackByField, showCategoryLegend,
    };
};

// ─── TOOLTIP: distributed (single-series, one colour per category) ────────────
const buildDistributedTooltip = (distColors, sharedUnit, unitsMap) => {
    return ({ series, seriesIndex, dataPointIndex, w }) => {
        const dp     = w.config.series[0]?.data?.[dataPointIndex];
        const label  = (dp && typeof dp === 'object' ? dp.x : null)
            ?? `Point ${dataPointIndex + 1}`;
        const rawVal = series[seriesIndex]?.[dataPointIndex]
            ?? (dp && typeof dp === 'object' ? dp.y : dp) ?? 0;
        const numVal = typeof rawVal === 'number' ? rawVal : (parseFloat(rawVal) || 0);
        const color  = (w.config.colors || distColors)[dataPointIndex % (distColors?.length || 1)];
        const unit   = sharedUnit || unitsMap?.[label] || '';
        return `
            <div style="padding:10px 14px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;
                box-shadow:0 4px 16px rgba(15,23,42,0.1);font-family:'DM Sans',system-ui,sans-serif;
                min-width:160px;max-width:280px;">
                <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">
                    <div style="width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0;margin-top:3px;"></div>
                    <span style="font-size:12.5px;font-weight:700;color:#0f172a;line-height:1.4;word-break:break-word;">${label}</span>
                </div>
                <div style="font-size:20px;font-weight:900;color:#0f172a;font-family:'DM Mono',monospace;letter-spacing:-0.02em;line-height:1;">
                    ${formatNumberWithSuffix(numVal)}
                    ${unit ? `<span style="font-size:11px;font-weight:500;color:#94a3b8;margin-left:5px;">${unit}</span>` : ''}
                </div>
            </div>`;
    };
};

// ─────────────────────────────────────────────────────────────────────────────
//  TOOLTIP: multi-series — handles BOTH unit-on-xAxis and unit-on-stackBy
//
//  Case A — unit on stackByField  →  series[].unit set, seriesUnitMap populated
//    categoryUnit = '' (unitsMap keyed by stack values, not x-axis categories)
//    rowUnit = '' || seriesUnitMap[name]  →  correct per-series unit           ✓
//
//  Case B — unit on xAxisField  →  series[].unit = null, seriesUnitMap empty
//    categoryUnit = unitsMap[categoryLabel]  (e.g. 'PNG' → 'SCM')
//    rowUnit = categoryUnit  →  correct per bar group                          ✓
//
//  Rule: categoryUnit wins when non-empty (x-axis keyed, always correct).
//        seriesUnitMap only consulted when categoryUnit is empty (Case A).
// ─────────────────────────────────────────────────────────────────────────────
const buildStackedTooltip = (categoriesArr, seriesColors, seriesArr, sharedUnit, unitsMap) => {
    const seriesUnitMap = {};
    (seriesArr || []).forEach(s => {
        if (s.unit) {
            seriesUnitMap[String(s.name)]                     = s.unit;
            seriesUnitMap[sanitiseSeriesName(String(s.name))] = s.unit;
        }
    });

    return ({ series, dataPointIndex, w }) => {
        const categoryLabel = (categoriesArr || [])[dataPointIndex] ?? `#${dataPointIndex}`;

        // Case B: unit keyed by x-axis category value (e.g. 'PNG' → 'SCM')
        const categoryUnit = unitsMap?.[categoryLabel] || '';

        const rows = (w.config.series || []).map((s, si) => {
            const val   = series[si]?.[dataPointIndex];
            const color = (w.config.colors || seriesColors)[si] || '#94a3b8';
            const name  = sanitiseSeriesName(s.name);
            if (val === null || val === undefined || val === 0) return '';
            const rowUnit = categoryUnit || seriesUnitMap[s.name] || seriesUnitMap[name] || '';
            const labelSuffix = (!categoryUnit && rowUnit)
                ? ` <span style="font-size:10px;color:#94a3b8;">(${rowUnit})</span>`
                : '';

            return `
                <div style="display:flex;align-items:center;gap:8px;padding:2px 0;">
                    <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
                    <span style="font-size:12px;color:#475569;flex:1;">${name}${labelSuffix}:</span>
                    <span style="font-size:12px;font-weight:700;color:#0f172a;font-family:'DM Mono',monospace;margin-left:8px;">
                        ${formatNumberWithSuffix(val)}
                        ${rowUnit ? `<span style="font-size:10px;font-weight:500;color:#94a3b8;margin-left:3px;">${rowUnit}</span>` : ''}
                    </span>
                </div>`;
        }).filter(Boolean).join('');

        const headerUnit = sharedUnit || categoryUnit || (() => {
            const all = [...new Set(
                (w.config.series || []).map(s => {
                    const n = sanitiseSeriesName(s.name);
                    return seriesUnitMap[s.name] || seriesUnitMap[n] || '';
                }).filter(Boolean)
            )];
            return all.length === 1 ? all[0] : '';
        })();

        return `
            <div style="padding:10px 14px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;
                box-shadow:0 4px 16px rgba(15,23,42,0.1);font-family:'DM Sans',system-ui,sans-serif;
                min-width:190px;max-width:310px;">
                <div style="font-size:12.5px;font-weight:700;color:#0f172a;margin-bottom:8px;
                    padding-bottom:6px;border-bottom:1px solid #f1f5f9;">
                    ${categoryLabel}
                    ${headerUnit ? `<span style="font-size:10px;font-weight:500;color:#94a3b8;margin-left:5px;">(${headerUnit})</span>` : ''}
                </div>
                ${rows || '<div style="font-size:11px;color:#94a3b8;">No data</div>'}
            </div>`;
    };
};

// ─── LEGEND FORMATTERS ────────────────────────────────────────────────────────
const buildDistributedLegend = (baseConfig, categories, distColors, colorMap) => ({
    ...baseConfig.legend,
    customLegendItems: categories?.length ? categories : Object.keys(colorMap || {}),
    markers: {
        fillColors: categories?.length ? distColors : Object.values(colorMap || {}),
        width: 10, height: 10, radius: 50,
    },
    formatter: (name) => sanitiseSeriesName(name),
});

const buildStackedLegend = (baseConfig, seriesArr, sharedUnit) => ({
    ...baseConfig.legend,
    position: 'bottom', horizontalAlign: 'center',
    markers: { width: 10, height: 10, radius: 50 },
    itemMargin: { horizontal: 16, vertical: 8 },
    formatter: (seriesName) => {
        const clean = sanitiseSeriesName(seriesName);
        if (sharedUnit) return clean;
        const s = (seriesArr || []).find(x => x.name === seriesName);
        return s?.unit ? `${clean} (${s.unit})` : clean;
    },
});

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export const HorizontalBarChart = ({ chartData, chartOptions = {}, groupedField = [] }) => {
    const data = chartOptions.transform
        ? transformData(chartData?.data, groupedField, chartData?.title, chartData?.dataSourceType)
        : chartData;

    if (data?.errorMessage) return <ErrorMessage message={data.errorMessage} />;
    if (!data?.series?.length) return <ErrorMessage />;

    const toolbarVisible = chartOptions.toolbarVisible !== false;
    const isStacked      = data.stacked != null ? !!data.stacked : chartOptions.stacked !== false;
    const seriesColors   = chartOptions.colors || getSeriesColors(data.series, data.colorMap, data.colors);
    const sharedUnit     = getSingleUnit(data.unitsMap);
    const baseConfig     = getBaseChartConfig('light', seriesColors, data, toolbarVisible);

    // Dynamic height
    const catCount    = data.categories?.length || data.series?.[0]?.data?.length || 6;
    const seriesCount = isStacked ? 1 : (data.series?.length || 1);
    const rowSlot     = Math.max(60, seriesCount * 32 + 20);
    const chartHeight = chartOptions.height || Math.max(280, catCount * rowSlot + 110);

    const barHeightPct = chartOptions.barHeight
        ? (typeof chartOptions.barHeight === 'number' ? `${chartOptions.barHeight}%` : chartOptions.barHeight)
        : (isStacked ? '65%' : `${Math.min(75, Math.max(20, Math.floor(70 / seriesCount)))}%`);

    // ★ FIX: mirror BarChart's showCategoryLegend && !isStacked pattern.
    //   The previous check used data?.groupedField?.length === 1 which was always
    //   falsy because groupedField is never stored on the data object itself.
    const showCategoryLegend = data.showCategoryLegend && !isStacked;
    const distColors         = showCategoryLegend && data.colorMap
        ? Object.values(data.colorMap)
        : seriesColors;

    // ★ FIX: same hasMixedUnits pattern as BarChart for clear, consistent gating.
    const hasSeriesUnits   = data.series.some(s => !!s.unit);
    const hasCategoryUnits = !sharedUnit && data.unitsMap && Object.keys(data.unitsMap).length > 0;
    const hasMixedUnits    = !sharedUnit && (hasSeriesUnits || hasCategoryUnits);

    // Tooltip selection — mirrors BarChart exactly:
    //   showCategoryLegend → distributed (one colour per bar)
    //   hasMixedUnits      → stacked/multi-series with per-unit resolution
    //   otherwise          → ApexCharts default
    const customTooltip = showCategoryLegend
        ? buildDistributedTooltip(distColors, sharedUnit, data.unitsMap)
        : hasMixedUnits
            ? buildStackedTooltip(data.categories, seriesColors, data.series, sharedUnit, data.unitsMap)
            : undefined;

    // Legend selection
    const legendConfig = showCategoryLegend
        ? buildDistributedLegend(baseConfig, data.categories, distColors, data.colorMap)
        : buildStackedLegend(baseConfig, data.series, sharedUnit);

    const chartConfig = {
        ...baseConfig,
        chart: {
            ...baseConfig.chart, type: 'bar', height: chartHeight,
            stacked: isStacked, stackType: chartOptions.stackType || 'normal',
        },
        colors: showCategoryLegend ? distColors : seriesColors,
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: barHeightPct,
                borderRadius: (isStacked && !showCategoryLegend) ? 0 : 4,
                borderRadiusApplication: 'end', borderRadiusWhenStacked: 'last',
                distributed: showCategoryLegend,
                dataLabels: { position: 'center', hideOverflowingLabels: true },
            },
        },
        dataLabels: {
            enabled: chartOptions.showDataLabels !== false,
            formatter: (val) => (!val || val === 0) ? '' : formatNumberWithSuffix(val),
            style: { fontSize: '10px', fontWeight: 500, colors: ['#475569'] },
            dropShadow: { enabled: true, top: 1, left: 1, blur: 2, color: '#00000040', opacity: 0.4 },
        },
        stroke: { show: true, width: (isStacked && !showCategoryLegend) ? 1 : 2, colors: ['#ffffff'] },
        xaxis: (() => {
            // ★ FIX: compute precision from actual data so tick labels are never repeated.
            //   e.g. max=0.01 → needs 2 decimal places → tickAmount=2 (just "0" and "0.01")
            const allVals = (data.series || []).flatMap(s =>
                Array.isArray(s.data)
                    ? s.data.map(d => Math.abs(typeof d === 'object' ? (d?.y ?? 0) : (d ?? 0)))
                    : []
            );
            const maxVal   = Math.max(...allVals, 0);
            const decimals = maxVal === 0 ? 0
                : maxVal >= 1 ? 0
                : Math.abs(Math.floor(Math.log10(maxVal))) + 1;  // 0.01→2, 0.001→3
            const factor      = Math.pow(10, decimals);
            const uniqueSteps = maxVal === 0 ? 2 : Math.min(5, Math.max(2, Math.round(maxVal * factor)));

            return {
                categories: data.categories || [],
                tickPlacement: 'on',
                tickAmount: uniqueSteps,
                labels: {
                    style: monoLabelStyle,
                    formatter: (val) => {
                        if (val === null || val === undefined) return '';
                        const num = typeof val === 'number' ? val : parseFloat(val);
                        if (isNaN(num)) return String(val);
                        if (num === 0)  return '0';
                        if (Math.abs(num) >= 1000) return formatNumberWithSuffix(num);
                        if (Math.abs(num) >= 1)    return num % 1 === 0 ? String(num) : num.toFixed(1);
                        // sub-1 values: show exactly enough decimals to be non-zero and unique
                        return parseFloat(num.toFixed(decimals)).toString();
                    },
                },
                axisBorder: { show: true },
                axisTicks:  { show: true },
            };
        })(),
        yaxis: {
            axisBorder: {
                show: true,
                color: '#94a3b8',
                width: 1
            },
            axisTicks: {
                show: true,
                borderType: 'solid',
                color: '#94a3b8',
                width: 6,
                offsetX: 0
            },
            labels: {
                style: {
                    ...axisLabelStyle,
                    fontSize: '12px',
                    fontWeight: 600,
                    colors: '#374151'
                },
                maxWidth: chartOptions.yAxisLabelWidth || 150,
                offsetX: 0
            }
        },
        grid: {
            ...baseConfig.grid,
            xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } },
            padding: { top: 0, right: 24, bottom: 8, left: 12 },
        },
        tooltip: {
            ...baseConfig.tooltip,
            shared: !showCategoryLegend,
            intersect: showCategoryLegend,
            ...(customTooltip
                ? { custom: customTooltip }
                : {
                    y: {
                        formatter: (val) => { const f = formatNumberWithSuffix(val); return sharedUnit ? `${f} ${sharedUnit}` : f; },
                        title: { formatter: (n) => { const c = sanitiseSeriesName(n); return c ? `${c}:` : ''; } },
                    },
                }
            ),
        },
        legend: legendConfig,
        states: { hover: { filter: { type: 'darken', value: 0.88 } }, active: { filter: { type: 'darken', value: 0.80 } } },
    };

    return (
        <ChartAnimationWrapper variant="fadeRight" duration={700}>
            <style>{`
                .apexcharts-tooltip {
                    z-index: 9999 !important;
                    overflow: visible !important;
                    position: fixed !important;
                }
                .apexcharts-xaxistooltip {
                    z-index: 9999 !important;
                }
            `}</style>
            <div style={{ overflowX: 'hidden', overflowY: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                <ReactApexChart options={chartConfig} series={data.series} type="bar" height={chartHeight} />
            </div>
        </ChartAnimationWrapper>
    );
};